import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Check, ChevronsUpDown } from "lucide-react";

/**
 * Debounced server-side customer search.
 *
 * @param value              Selected customer object (or {id, party_name})
 * @param onSelect           Called with the full customer doc from the API
 * @param testId             data-testid for the trigger
 * @param initialCustomers   Optional prefetched list (used as default popover contents)
 * @param placeholder        Empty-state label
 */
export default function CustomerCombobox({ value, onSelect, testId, initialCustomers = [], placeholder = "Select customer…" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(initialCustomers.slice(0, 50));
  const [loading, setLoading] = useState(false);
  // We keep the last picked customer here so the trigger label stays correct
  // even after the parent trims / paginates its prefetched `initialCustomers`.
  const [picked, setPicked] = useState(value || null);
  const timerRef = useRef(null);
  const abortRef = useRef(null);
  const reqIdRef = useRef(0);

  // Keep local `picked` in sync with any parent-controlled selection changes
  useEffect(() => { if (value && value.id !== picked?.id) setPicked(value); }, [value, picked?.id]);

  // Debounce every keystroke into a `/customers?q=` fetch. Empty query returns
  // the top of the list (limit 50) so operators see something before typing.
  // AbortController + request-id guarantee stale responses cannot overwrite
  // newer rows (out-of-order network races).
  useEffect(() => {
    if (!open) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      if (abortRef.current) abortRef.current.abort();
      abortRef.current = new AbortController();
      const myReq = ++reqIdRef.current;
      setLoading(true);
      try {
        const { data } = await api.get("/customers", {
          params: { q, limit: 50 },
          signal: abortRef.current.signal,
        });
        if (myReq === reqIdRef.current) setRows(data);
      } catch { /* toasted globally; aborted requests silently ignored */ }
      finally { if (myReq === reqIdRef.current) setLoading(false); }
    }, q ? 250 : 0);
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [q, open]);

  // Reset query + rows when the popover closes so a re-open starts clean.
  const handleOpenChange = (o) => {
    setOpen(o);
    if (!o) { setQ(""); setRows(initialCustomers.slice(0, 50)); }
  };

  const active = picked || value;
  const label = active?.party_name
    ? `${active.party_name}${active.party_code ? ` · ${active.party_code}` : ""}`
    : placeholder;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          data-testid={testId}
          className="h-9 w-full justify-between rounded-sm border-neutral-300 text-sm font-normal"
        >
          <span className={active?.party_name ? "" : "text-neutral-400"}>{label}</span>
          <ChevronsUpDown size={14} className="text-neutral-500 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="p-0 w-[--radix-popover-trigger-width] min-w-[320px]">
        <Command shouldFilter={false}>
          <CommandInput
            data-testid={`${testId}-input`}
            placeholder="Search customer name, code or GSTIN…"
            value={q}
            onValueChange={setQ}
          />
          <CommandList>
            {loading && <div className="px-3 py-2 text-xs text-neutral-500">Searching…</div>}
            {!loading && rows.length === 0 && <CommandEmpty>No customer matches.</CommandEmpty>}
            <CommandGroup>
              {rows.map((c) => (
                <CommandItem
                  key={c.id}
                  value={c.id}
                  data-testid={`${testId}-option-${c.id}`}
                  onSelect={() => { setPicked(c); onSelect(c); setOpen(false); setQ(""); }}
                  className="text-sm"
                >
                  <Check size={13} className={`mr-2 ${active?.id === c.id ? "opacity-100" : "opacity-0"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="truncate">{c.party_name}</div>
                    <div className="text-[10px] text-neutral-500 font-mono-num truncate">
                      {c.party_code || "—"}{c.location ? ` · ${c.location}` : ""}{c.gstin ? ` · ${c.gstin}` : ""}
                    </div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
